// src/pages/public/Products.jsx
// Page publique /produits — même design que la Home Tapio.

import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowRight,
  ArrowUp,
  CheckCircle2,
  ShoppingBag,
  Loader2,
  Star,
  Wifi,
  UtensilsCrossed,
  Gift,
  Sparkles,
  Package,
  ShieldCheck,
  ShoppingCart,
} from 'lucide-react'
import api from '../../lib/axios'
import { useCart } from '../../hooks/useCart'

/* ---------- Design tokens (identiques à la Home) ---------- */
const C = {
  bg: '#f9f9ff',
  bgAlt: '#eff2ff',
  ink: '#000a1e',
  body: '#3c4148',
  primary: '#006b5f',
  primaryHover: '#005a50',
  chip: '#e6ebff',
  navy: '#101f3f',
  pro: '#000617',
  gold: '#c9a24a',
}

const card = 'bg-white rounded-3xl shadow-[0_4px_24px_rgba(15,23,42,0.06)]'
const lift = 'transition-all duration-300 ease-out hover:-translate-y-2 hover:scale-[1.02] hover:shadow-[0_18px_44px_rgba(15,23,42,0.13)]'
const press = 'transition-all duration-200 ease-out hover:scale-105 active:scale-95 cursor-pointer focus:outline-none focus-visible:ring-4 focus-visible:ring-[#006b5f]/30'


/* ---------- Catégories ---------- */
const CATEGORIES = [
  { id: 'all', label: 'Tous les produits', icon: ShoppingBag },
  { id: 'google_review', label: 'Avis Google', icon: Star },
  { id: 'menu', label: 'Menus', icon: UtensilsCrossed },
  { id: 'nfc_card', label: 'Cartes NFC', icon: Wifi },
  { id: 'loyalty', label: 'Fidélité', icon: Gift },
  { id: 'pack', label: 'Packs', icon: Sparkles },
]

const CATEGORY_LABELS = {
  all: 'Tous',
  google_review: 'Avis Google',
  menu: 'Menu',
  nfc_card: 'Cartes NFC',
  loyalty: 'Fidélité',
  pack: 'Packs',
}

const CATEGORY_ICONS = {
  google_review: Star,
  menu: UtensilsCrossed,
  nfc_card: Wifi,
  loyalty: Gift,
  pack: Sparkles,
}

const fmt = (n, currency = 'MAD') => {
  const symbol = currency === 'MAD' ? 'DH' : currency === 'EUR' ? '€' : currency
  return Number(n).toFixed(0) + ' ' + symbol
}

/* ---------- Hooks ---------- */
function useInView(threshold = 0.15) {
  const ref = useRef(null)
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (typeof IntersectionObserver === 'undefined') { setVisible(true); return }
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisible(true); io.disconnect() }
    }, { threshold })
    io.observe(el)
    return () => io.disconnect()
  }, [threshold])
  return [ref, visible]
}

function Reveal({ children, delay = 0, className = '' }) {
  const [ref, visible] = useInView()
  return (
    <div
      ref={ref}
      style={{ transitionDelay: visible ? `${delay}ms` : '0ms' }}
      className={`transition-all duration-700 ease-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`}
    >
      {children}
    </div>
  )
}

/* ---------- Carte produit ---------- */
function ProductCard({ product, onAdd, delay = 0 }) {
  const Icon = CATEGORY_ICONS[product.category] || ShoppingBag
  const categoryLabel = CATEGORY_LABELS[product.category] || product.category

  return (
    <Reveal delay={delay}>
      <div className={`${card} ${lift} group overflow-hidden flex flex-col h-full`}>
        {/* Image */}
        <div className="aspect-[4/3] bg-[#eff2ff] relative overflow-hidden">
          {product.image ? (
            <img
              src={product.image}
              alt={product.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
              onError={(e) => { e.currentTarget.style.display = 'none' }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Icon className="w-16 h-16 text-[#006b5f]/30" />
            </div>
          )}

          {/* Badge catégorie */}
          <div className="absolute top-3 left-3 px-3 py-1 bg-white/95 backdrop-blur rounded-full text-[10px] font-bold text-[#000a1e] uppercase tracking-wider shadow-sm">
            {categoryLabel}
          </div>

          {/* Badge populaire */}
          {product.is_featured && (
            <div className="absolute top-3 right-3 px-2.5 py-1 bg-[#c9a24a] text-white text-[10px] font-bold rounded-full flex items-center gap-1 shadow-sm">
              <Star className="w-3 h-3 fill-white" />
              POPULAIRE
            </div>
          )}
        </div>

        {/* Contenu */}
        <div className="p-6 flex-1 flex flex-col">
          <h3 className="font-display font-semibold text-xl text-[#000a1e] leading-snug mb-2">
            {product.name}
          </h3>
          <p className="text-[14px] text-[#3c4148] leading-relaxed mb-5 line-clamp-2">
            {product.short_description}
          </p>

          {/* Features */}
          {product.features?.length > 0 && (
            <ul className="space-y-2 mb-5">
              {product.features.slice(0, 3).map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-[13px] text-[#000a1e]">
                  <CheckCircle2 className="w-4 h-4 text-[#006b5f] shrink-0 mt-0.5" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          )}

          {/* Prix + CTA */}
          <div className="mt-auto flex items-center justify-between gap-3 pt-5 border-t border-slate-100">
            <div>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Prix</p>
              <p className="font-display font-extrabold text-2xl text-[#000a1e] mt-0.5">
                {fmt(product.price, product.currency)}
              </p>
            </div>
            <button
              onClick={() => onAdd(product)}
              className={`${press} shine relative overflow-hidden flex items-center gap-2 bg-[#006b5f] hover:bg-[#005a50] text-white font-display font-semibold px-5 py-3 rounded-xl shadow-lg shadow-[#006b5f]/20 text-sm`}
            >
              <ShoppingBag className="w-4 h-4" />
              Ajouter
            </button>
          </div>
        </div>
      </div>
    </Reveal>
  )
}

/* ---------- Page ---------- */
export default function Products() {
  const cart = useCart()   // 🆕

  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState('all')
  const [toast, setToast] = useState('')
  const [progress, setProgress] = useState(0)
  const [showTop, setShowTop] = useState(false)
  const toastTimer = useRef(null)

  const notify = useCallback((msg) => {
    setToast(msg)
    clearTimeout(toastTimer.current)
    toastTimer.current = setTimeout(() => setToast(''), 3200)
  }, [])
  useEffect(() => () => clearTimeout(toastTimer.current), [])

  useEffect(() => {
    let cancelled = false
    api.get('/public/products')
      .then(({ data }) => {
        if (cancelled) return
        setProducts(data?.data ?? [])
      })
      .catch(() => {
        if (!cancelled) setError('Impossible de charger les produits.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight
      setProgress(h > 0 ? Math.min(100, (window.scrollY / h) * 100) : 0)
      setShowTop(window.scrollY > 600)
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const filtered = filter === 'all' ? products : products.filter((p) => p.category === filter)

  return (
    <div className="min-h-screen font-body text-[#3c4148] antialiased overflow-x-hidden" style={{ background: C.bg }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:ital,wght@0,400;0,500;0,600;0,700;1,400&family=Plus+Jakarta+Sans:wght@500;600;700;800&display=swap');
        .font-display{font-family:'Plus Jakarta Sans',system-ui,sans-serif}
        .font-body{font-family:'Inter',system-ui,sans-serif}
        @keyframes fadeIn{from{opacity:0}to{opacity:1}}
        @keyframes pop{0%{opacity:0;transform:scale(.92) translateY(8px)}100%{opacity:1;transform:scale(1) translateY(0)}}
        @keyframes heroIn{from{opacity:0;transform:translateY(28px)}to{opacity:1;transform:translateY(0)}}
        @keyframes toastIn{from{opacity:0;transform:translate(-50%,20px) scale(.95)}to{opacity:1;transform:translate(-50%,0) scale(1)}}
        @keyframes pulseRing{0%{box-shadow:0 0 0 0 rgba(0,107,95,.35)}100%{box-shadow:0 0 0 22px rgba(0,107,95,0)}}
        @keyframes shine{0%{transform:translateX(-120%) skewX(-20deg)}100%{transform:translateX(320%) skewX(-20deg)}}
        @keyframes blob{0%,100%{transform:scale(1) translate(0,0)}50%{transform:scale(1.15) translate(-20px,-16px)}}
        .animate-fade-in{animation:fadeIn .25s ease-out both}
        .animate-pop{animation:pop .3s ease-out both}
        .animate-hero{animation:heroIn .8s cubic-bezier(.2,.7,.2,1) both}
        .animate-toast{animation:toastIn .35s ease-out both}
        .animate-pulse-ring{animation:pulseRing 1.6s ease-out infinite}
        .animate-blob{animation:blob 12s ease-in-out infinite}
        .shine::after{content:'';position:absolute;top:0;left:0;height:100%;width:35%;background:linear-gradient(90deg,transparent,rgba(255,255,255,.35),transparent);transform:translateX(-120%) skewX(-20deg)}
        .shine:hover::after{animation:shine .9s ease-out}
      `}</style>

      {/* Barre de progression */}
      <div className="fixed top-0 left-0 h-[3px] z-[60] bg-[#006b5f] transition-[width] duration-150" style={{ width: `${progress}%` }} />

      {/* HERO */}
      <section className="relative overflow-hidden pt-16 pb-20">
        <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_40%_45%,rgba(215,225,255,0.55),transparent_55%)] animate-blob" />
        <div className="relative max-w-7xl mx-auto px-6 text-center space-y-6">
          <div className="animate-hero inline-flex items-center gap-2 bg-[#e9edff] px-4 py-1.5 rounded-full text-xs">
            <Sparkles className="w-3.5 h-3.5 text-[#006b5f]" />
            <span className="font-semibold text-[#006b5f]">Nos produits</span>
            <span className="text-slate-500">• Supports NFC premium</span>
          </div>

          <h1 className="animate-hero font-display font-bold text-[#000a1e] tracking-tight text-4xl sm:text-5xl lg:text-[56px] leading-[1.1]" style={{ animationDelay: '120ms' }}>
            Des supports NFC pour <span className="text-[#006b5f]">booster votre business</span>
          </h1>

          <p className="animate-hero text-lg leading-relaxed max-w-2xl mx-auto" style={{ animationDelay: '240ms' }}>
            Cartes NFC, chevalets Google Review, menus digitaux et packs complets.
            Livrés préprogrammés, prêts à l'emploi, personnalisables à volonté.
          </p>

          <div className="animate-hero flex flex-wrap gap-x-6 gap-y-2 justify-center text-xs font-medium text-[#1f2937] pt-2" style={{ animationDelay: '360ms' }}>
            {['Livraison 48h offerte', 'Personnalisation incluse', 'Garantie 30 jours'].map((t) => (
              <span key={t} className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#006b5f]" />{t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FILTRES */}
      <section className="relative z-10 -mt-8 pb-8">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <div className="bg-white rounded-2xl shadow-[0_4px_24px_rgba(15,23,42,0.06)] p-2 flex gap-1 overflow-x-auto">
              {CATEGORIES.map((cat) => {
                const Icon = cat.icon
                const active = filter === cat.id
                return (
                  <button
                    key={cat.id}
                    onClick={() => setFilter(cat.id)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition-all ${
                      active
                        ? 'bg-[#006b5f] text-white shadow-md shadow-[#006b5f]/20'
                        : 'text-[#3c4148] hover:bg-[#eff2ff]'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {cat.label}
                  </button>
                )
              })}
            </div>
          </Reveal>
        </div>
      </section>

      {/* LISTE PRODUITS */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-6">
          {loading && (
            <div className="flex justify-center py-24">
              <Loader2 className="w-8 h-8 text-[#006b5f] animate-spin" />
            </div>
          )}

          {error && (
            <div className="max-w-lg mx-auto p-4 bg-red-50 border border-red-100 rounded-2xl text-sm text-red-600 text-center">
              {error}
            </div>
          )}

          {!loading && !error && filtered.length === 0 && (
            <div className="text-center py-20">
              <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Aucun produit dans cette catégorie.</p>
            </div>
          )}

          {!loading && filtered.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((product, i) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onAdd={(p) => {
                    cart.addItem(p, 1)
                    notify(`${p.name} ajouté au panier`)
                  }}
                  delay={(i % 3) * 120}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA BANNER — même style navy que la Home */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <div className="relative overflow-hidden rounded-[32px] px-8 sm:px-16 py-14 shadow-[0_24px_50px_rgba(16,31,63,0.25)]" style={{ background: C.navy }}>
              <div className="absolute -bottom-32 -right-24 w-[520px] h-[520px] rounded-full bg-[radial-gradient(circle,rgba(20,120,110,0.35),transparent_65%)] animate-blob" />
              <div className="relative space-y-6 max-w-3xl">
                <span className="inline-flex items-center gap-2 bg-white/10 text-[#7fe9e0] text-xs font-semibold px-3 py-1.5 rounded-lg">
                  <ShieldCheck className="w-3.5 h-3.5" /> Devis flotte & personnalisation
                </span>
                <h2 className="font-display font-bold text-white text-4xl sm:text-5xl leading-tight tracking-tight">
                  Une question ? Besoin d'un devis ?
                </h2>
                <p className="text-[#8b9bc0]">
                  Notre équipe vous accompagne pour choisir les supports adaptés à votre commerce
                  et graver votre logo sur les cartes.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 pt-2">
                  <Link
                    to="/contact"
                    className={`${press} shine relative overflow-hidden flex items-center justify-between gap-6 bg-[#006b5f] hover:bg-[#005a50] text-white font-display font-semibold px-8 py-5 rounded-xl group`}
                  >
                    <span>Nous contacter</span>
                    <ArrowRight className="w-4 h-4 shrink-0 transition-transform duration-200 group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Bouton retour en haut */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        aria-label="Retour en haut"
        className={`fixed bottom-6 right-6 z-40 w-12 h-12 rounded-full bg-[#006b5f] text-white shadow-xl flex items-center justify-center transition-all duration-300 hover:scale-110 active:scale-90 ${showTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6 pointer-events-none'}`}
      >
        <ArrowUp className="w-5 h-5" />
      </button>

      {/* 🆕 Bouton flottant panier */}
      {cart.count > 0 && (
        <Link
          to="/panier"
          className="fixed bottom-6 left-6 z-50 flex items-center gap-3 bg-[#006b5f] hover:bg-[#005a50] text-white font-display font-semibold pl-5 pr-6 py-3.5 rounded-full shadow-2xl shadow-[#006b5f]/30 hover:scale-105 active:scale-95 transition-all"
        >
          <div className="relative">
            <ShoppingCart className="w-5 h-5" />
            <span className="absolute -top-2 -right-2 w-5 h-5 bg-[#c9a24a] text-[10px] font-bold rounded-full flex items-center justify-center">
              {cart.count}
            </span>
          </div>
          <span>Voir le panier</span>
          <ArrowRight className="w-4 h-4" />
        </Link>
      )}

      {/* Toast */}
      {toast && (
        <div role="status" className="animate-toast fixed bottom-6 left-1/2 z-[120] max-w-[90vw] bg-[#000a1e] text-white text-sm font-medium px-5 py-3 rounded-xl shadow-2xl flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-[#2dd4bf] shrink-0" />{toast}
        </div>
      )}
    </div>
  )
}